import React from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import dayjs, { Dayjs } from "dayjs";
import { CircularProgress } from "@mui/material";
import {
  DesktopFiltersProps,
  FilterData,
} from "../../components/DesktopFilters/DesktopFilters";
import { selectUser, selectUserRole } from "../../slices/user";
import DesktopDropView from "../../components/DesktopDropView";
import { selectToken } from "../../slices/token";
import { Context } from "../../context/ContextProvider/ContextProvider";
import api from "../../api";
import DesktopRegisterDetails from "../../components/DesktopRegisterDetails";
import { showError } from "../../utils/helpers";
import { getDateRange } from "../../utils/getDateRange";
import { CSVLink } from "react-csv";

const Logbook = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const isGuard = userRole === "guard";
  const token = useAppSelector(selectToken);
  const propertyId = user?.property?.id;
  const unitName = user?.unit?.name;
  const logbookUnitFilter = userRole === "host" || userRole === "secondary_host" ? unitName : undefined;
  const logbookVisitedUserFilter =
    userRole === "secondary_host" ? user?.name : undefined;
  const missingLogbookScope =
    ((userRole === "host" || userRole === "secondary_host") && !unitName) ||
    (userRole === "secondary_host" && !user?.name);
  const dispatch = useAppDispatch();

  const { selectedAccessPointGuard } = React.useContext(Context);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [selectedAccessPoint, setSelectedAccessPoint] =
    React.useState<string>("0");
  const [selectedState, setSelectedState] = React.useState<
    "" | "entry" | "exit"
  >("");
  const [selectedDateFilter, setSelectedDateFilter] =
    React.useState<string>("all_dates");
  const [showAccessDetails, setShowAccessDetails] =
    React.useState<boolean>(false);
  const [dateFilter, setDateFilter] = React.useState<Dayjs | null>(null);
  const [selectedAccessId, setSelectedAccessId] = React.useState<string | null>(
    null
  );
  const [accessesData, setAccessesData] = React.useState<any[]>([]);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [totalAccess, setTotalAccess] = React.useState<number>(0);
  const [dateRange, setDateRange] = React.useState<{ fromISO: string | undefined; toISO: string | undefined }>({ fromISO: undefined, toISO: undefined });
  const [csvExportData, setCsvExportData] = React.useState<
    Record<string, string>[]
  >([]);
  const [isExporting, setIsExporting] = React.useState(false);
  const csvLinkRef = React.useRef<{ link?: HTMLAnchorElement } | null>(null);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);
  const logbookRequestId = React.useRef(0);

  const handleDownloadCsv = React.useCallback(async () => {
    if (missingLogbookScope || !token || !propertyId) return;
    if (isGuard && (!Number.isInteger(Number(selectedAccessPointGuard)) || Number(selectedAccessPointGuard) <= 0)) return;
    let currentAccessPoint: number | undefined;
    if (!isGuard) {
      currentAccessPoint =
        selectedAccessPoint === "0"
          ? undefined
          : Number(selectedAccessPoint);
    } else {
      currentAccessPoint = selectedAccessPointGuard
        ? Number(selectedAccessPointGuard)
        : undefined;
    }
    try {
      setIsExporting(true);
      const response = await api.getAccessesRequest(
        propertyId,
        "createdAt",
        1,
        10000,
        currentAccessPoint,
        debouncedSearchFilter,
        logbookUnitFilter,
        dateRange.fromISO,
        dateRange.toISO,
        selectedState,
        logbookVisitedUserFilter,
      );
      if (!response?.data?.length) {
        setCsvExportData([]);
        setIsExporting(false);
        return;
      }
      const csvRows: Record<string, string>[] = response.data.map((item) => {
        const entranceDt = item?.entrance_transaction?.datetime;
        const exitDt = item?.exit_transaction?.datetime;
        const differentExitDate =
          entranceDt && exitDt
            ? dayjs(entranceDt).format("DD/MM") !==
              dayjs(exitDt).format("DD/MM")
            : false;
        const dateLabel =
          entranceDt && exitDt
            ? dayjs(entranceDt).date() === dayjs(exitDt).date() &&
              dayjs(entranceDt).month() === dayjs(exitDt).month()
              ? dayjs(entranceDt).format("DD/MMMM")
              : dayjs(entranceDt).format("DD/MMMM")
              : entranceDt
                ? dayjs(entranceDt).format("DD/MMMM")
                : "";
        const checkIn = entranceDt
          ? dayjs(entranceDt).format("h:mma")
          : "-";
        let checkOut = "-";
        if (exitDt) {
          checkOut = differentExitDate
            ? `${dayjs(exitDt).format("h:mma")} ${dayjs(exitDt).format("DD/MMMM")}`
            : dayjs(exitDt).format("h:mma");
        }
        const hasAccessKey =
          item?.entrance_transaction?.access_key_id ||
          item?.exit_transaction?.access_key_id;
        const typeLabel = item?.authorized
          ? hasAccessKey
            ? labels.invitation
            : labels.register
          : labels.not_authorized_lb;
        return {
          [labels.date]: dateLabel,
          [labels.visitor]: item?.visitor ?? "",
          [labels.host]: item?.user ?? "",
          [labels.unit]: item?.unit ?? "",
          [labels.check_in_time]: checkIn,
          [labels.check_out_time]: checkOut,
          [labels.type_of_registration]: typeLabel,
        };
      });
      setCsvExportData(csvRows);
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(
        dispatch,
        errorMessage ? errorMessage : labels.generic_error,
      );
      setIsExporting(false);
    }
  }, [
    missingLogbookScope,
    token,
    propertyId,
    isGuard,
    selectedAccessPoint,
    selectedAccessPointGuard,
    debouncedSearchFilter,
    logbookUnitFilter,
    logbookVisitedUserFilter,
    dateRange.fromISO,
    dateRange.toISO,
    selectedState,
    labels.date,
    labels.visitor,
    labels.host,
    labels.unit,
    labels.check_in_time,
    labels.check_out_time,
    labels.type_of_registration,
    labels.invitation,
    labels.register,
    labels.not_authorized_lb,
    labels.generic_error,
    dispatch,
  ]);

  React.useEffect(() => {
    if (csvExportData.length === 0) return;
    const timer = setTimeout(() => {
      const link = csvLinkRef.current?.link;
      if (link) {
        link.click();
      }
      setCsvExportData([]);
      setIsExporting(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [csvExportData]);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
      return {
        label: item?.name,
        value: `${item.id}`,
      };
    })
    : [];

  const datesFilterArray = [
    {
      label: labels.all_dates,
      value: "all_dates",
    },
    {
      label: labels.today,
      value: "today",
    },
    {
      label: labels.last_week,
      value: "last_week",
    },
    {
      label: labels.last_month,
      value: "last_month",
    },
    {
      label: labels.calendar,
      value: "calendar",
    },
  ];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const filters: DesktopFiltersProps = {
    filterData: [
      {
        type: "multidate",
        value: selectedDateFilter,
        setValue: setSelectedDateFilter,
        disabled: isLoading,
        tooltip: "",
        function: () => { },
        array: datesFilterArray,
        size: "small",
        icon: "",
        label: labels.access_points,
        multiDateValue: dateFilter,
        setMultiDateValue: setDateFilter,
      },
      // {
      //   type: "selector",
      //   value: selectedAccessPoint,
      //   setValue: setSelectedAccessPoint,
      //   disabled: isLoading,
      //   tooltip: "",
      //   function: () => {},
      //   array: accessPointsArray,
      //   size: "small",
      //   icon: "",
      //   label: labels.access_points,
      //   multiDateValue: null,
      //   setMultiDateValue: () => {},
      // },
      {
        type: "icon",
        value: selectedState,
        setValue: setSelectedState,
        disabled: isLoading,
        tooltip: labels.entries,
        function: () => { },
        array: [],
        size: "small",
        icon: "entypo:login",
        label: "entry",
        multiDateValue: null,
        setMultiDateValue: () => { },
      },
      {
        type: "icon",
        value: selectedState,
        setValue: setSelectedState,
        disabled: isLoading,
        tooltip: labels.exits,
        function: () => { },
        array: [],
        size: "small",
        icon: "entypo:log-out",
        label: "exit",
        multiDateValue: null,
        setMultiDateValue: () => { },
      },
      {
        type: "search",
        value: searchFilter,
        setValue: setSearchFilter,
        disabled: isLoading,
        tooltip: "",
        function: () => { },
        array: [],
        size: "small",
        icon: "",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => { },
      },
      {
        type: "function",
        value: "",
        setValue: () => { },
        disabled: isLoading || isExporting,
        tooltip: labels.download_content,
        function: handleDownloadCsv,
        array: [],
        size: "small",
        icon: "fa-solid:file-download",
        label: "",
        multiDateValue: null,
        setMultiDateValue: () => { },
      },
    ],
  };

  if (!isGuard) {
    const elementToAdd: FilterData = {
      type: "selector",
      value: selectedAccessPoint,
      setValue: setSelectedAccessPoint,
      disabled: isLoading,
      tooltip: "",
      function: () => { },
      array: accessPointsArray,
      size: "small",
      icon: "",
      label: labels.access_points,
      multiDateValue: null,
      setMultiDateValue: () => { },
    };

    filters.filterData?.splice(1, 0, elementToAdd);
  }

  React.useEffect(() => {
    const r = getDateRange(selectedDateFilter, dateFilter);
    setDateRange({ fromISO: r ? r.from.toISOString() : undefined, toISO: r ? r.to.toISOString() : undefined });
  }, [selectedDateFilter, dateFilter]);

  const getLogbookData = React.useCallback(
    async (debouncedText: string) => {
      const requestId = ++logbookRequestId.current;
      const guardPointId = Number(selectedAccessPointGuard);
      if (missingLogbookScope || !token || !propertyId || (isGuard && (!Number.isInteger(guardPointId) || guardPointId <= 0))) {
        setAccessesData([]);
        setTableDataToDisplay(null);
        setTotalAccess(0);
        setIsLoading(false);
        return;
      }
      if (token && propertyId) {
        let currentAccessPoint;
        if (!isGuard) {
          if (selectedAccessPoint === "0") {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPoint);
          }
        } else {
          if (!selectedAccessPointGuard) {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPointGuard);
          }
        }
        try {
          setIsLoading(true);
          const response = await api.getAccessesRequest(
            propertyId,
            "createdAt",
            page + 1,
            rowsPerPage,
            currentAccessPoint,
            debouncedText,
            logbookUnitFilter,
            dateRange.fromISO,
            dateRange.toISO,
            selectedState,
            logbookVisitedUserFilter,
          );
          if (requestId !== logbookRequestId.current) return;
          if (response) {
            setTotalAccess(response?.meta?.pagination?.total);
            setAccessesData(response.data);
            const dataForTable: TableData = {
              header: [
                {
                  label: labels.date,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.visitor,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.host,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.unit,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.check_in_time,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.check_out_time,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.type_of_registration,
                  isIcon: false,
                  tooltipLabel: "",
                },
              ],
              body: response.data.map((item) => {
                const differentExitDate = item?.exit_transaction
                  ? dayjs(
                    item?.entrance_transaction?.datetime
                  ).format("DD/MM") !==
                  dayjs(
                    item?.exit_transaction?.datetime
                  ).format("DD/MM")
                  : false;
                return {
                  row: item?.id,
                  cells: [
                    {
                      label:
                        dayjs(
                          item?.entrance_transaction?.datetime
                        ).date() ===
                          dayjs(
                            item?.exit_transaction?.datetime
                          ).date() &&
                          dayjs(
                            item?.entrance_transaction?.datetime
                          ).month() ===
                          dayjs(
                            item?.exit_transaction?.datetime
                          ).month()
                          ? [
                            {
                              mainLabel: dayjs(
                                item?.entrance_transaction?.datetime
                              ).format("DD/MMMM"),
                              tooltipLabel: "",
                            },
                          ]
                          : [
                            {
                              mainLabel: `${dayjs(
                                item?.entrance_transaction?.datetime
                              ).format("DD/MMMM")}`,
                              tooltipLabel: "",
                            },
                          ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.visitor,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.user,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.unit,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label:
                        item?.entrance_transaction !== null
                          ? [
                            {
                              mainLabel: dayjs(
                                item?.entrance_transaction?.datetime
                              ).format("h:mma"),
                              tooltipLabel: "",
                            },
                          ]
                          : [
                            {
                              mainLabel: "-",
                              tooltipLabel: "",
                            },
                          ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label:
                        item?.exit_transaction !== null
                          ? !differentExitDate
                            ? [
                              {
                                mainLabel: item?.exit_transaction
                                  ? dayjs(
                                    item?.exit_transaction?.datetime
                                  ).format("h:mma")
                                  : "",
                                tooltipLabel: "",
                              },
                            ]
                            : [
                              {
                                mainLabel: item?.exit_transaction
                                  ? dayjs(
                                    item?.exit_transaction?.datetime
                                  ).format("h:mma")
                                  : "",
                                tooltipLabel: "",
                              },
                              {
                                mainLabel: item?.exit_transaction
                                  ? dayjs(
                                    item?.exit_transaction?.datetime
                                  ).format("DD/MMMM")
                                  : "",
                                tooltipLabel: "",
                              },
                            ]
                          : [
                            {
                              mainLabel: "-",
                              tooltipLabel: "",
                            },
                          ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label: item.authorized
                        ? [
                          {
                            mainLabel:
                              item?.entrance_transaction?.access_key_id ||
                                item?.exit_transaction?.access_key_id
                                ? "mdi:envelope"
                                : "mdi:clipboard",
                            tooltipLabel:
                              item?.entrance_transaction?.access_key_id ||
                                item?.exit_transaction?.access_key_id
                                ? labels.invitation
                                : labels.register,
                          },
                        ]
                        : [
                          {
                            mainLabel: "entypo:block",
                            tooltipLabel: labels.not_authorized_lb,
                          },
                          {
                            mainLabel:
                              item?.entrance_transaction?.access_key_id ||
                                item?.exit_transaction?.access_key_id
                                ? "mdi:envelope"
                                : "mdi:clipboard",
                            tooltipLabel:
                              item?.entrance_transaction?.access_key_id ||
                                item?.exit_transaction?.access_key_id
                                ? labels.invitation
                                : labels.register,
                          },
                        ],
                      isIcon: true,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                  ],
                };
              }),
            };
            setTableDataToDisplay(dataForTable);
          }
        } catch (error: any) {
          if (requestId !== logbookRequestId.current) return;
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error
          );
        } finally {
          if (requestId === logbookRequestId.current) setIsLoading(false);
        }
      }
    },
    [
      missingLogbookScope,
      token,
      propertyId,
      isGuard,
      selectedAccessPoint,
      selectedAccessPointGuard,
      page,
      rowsPerPage,
      logbookUnitFilter,
      logbookVisitedUserFilter,
      dateRange.fromISO,
      dateRange.toISO,
      selectedState,
      labels.date,
      labels.visitor,
      labels.host,
      labels.unit,
      labels.check_in_time,
      labels.check_out_time,
      labels.type_of_registration,
      labels.invitation,
      labels.register,
      labels.not_authorized_lb,
      labels.generic_error,
      dispatch,
    ]
  );

  React.useEffect(() => {
    getLogbookData(debouncedSearchFilter);
    return () => { logbookRequestId.current += 1; };
  }, [debouncedSearchFilter, getLogbookData]);

  React.useEffect(() => {
    setPage(0);
  }, [
    selectedDateFilter,
    dateFilter,
    selectedState,
    selectedAccessPoint,
    selectedAccessPointGuard,
    debouncedSearchFilter,
  ]);

  const csvHeaders = [
    { label: labels.date, key: labels.date },
    { label: labels.visitor, key: labels.visitor },
    { label: labels.host, key: labels.host },
    { label: labels.unit, key: labels.unit },
    { label: labels.check_in_time, key: labels.check_in_time },
    { label: labels.check_out_time, key: labels.check_out_time },
    { label: labels.type_of_registration, key: labels.type_of_registration },
  ];

  return (
    <div className="w-100">
      {csvExportData.length > 0 && (
        <CSVLink
          ref={csvLinkRef as any}
          data={csvExportData}
          headers={csvHeaders}
          filename="bitacora.csv"
          style={{ display: "none" }}
        />
      )}
      <DesktopHeader title={labels.logbook} isGuard={isGuard} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <TableComponent
              selectableRow={true}
              data={tableDataToDisplay}
              onPressRow={(id: number) => {
                setSelectedAccessId(
                  accessesData.find((item) => item?.id === id)?.documentId ??
                    null,
                );
                setShowAccessDetails(true);
              }}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              total={totalAccess}
              tableHeight={"calc(100vh - 188px)"}
              emptyMessage={labels.no_logs_registered}
            />
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={showAccessDetails}
        setShowView={setShowAccessDetails}
        content={
          <div className="flex h-100 border-box" style={{ width: 500 }}>
            <div className="w-100 h-100 border-box p-8 scroll">
              <DesktopRegisterDetails
                selectedAccessId={selectedAccessId}
                isMobile={false}
              />
            </div>
          </div>
        }
        extraFunc={false}
        title={labels.visit_details}
      />
    </div>
  );
};

export default Logbook;
